/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import { Button, Input } from "../../../components";
import { useAppSelector } from "../../../redux/Hooks/Hook";
import { SellInventoryType } from "../../../utilities/types";
import * as yup from "yup";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { CreateSellingProduct } from "../../../utilities/api/stock/sell";
import { useNavigate } from "react-router-dom";
import {
  getPercentValue,
  handleCgstChange,
  handleIgstChange,
  handleSgstChange,
  numberValidation,
} from "../../../utilities/utils";
import { messages } from "../../../utilities/Messages";

const getMessage = messages.stocks;

const AddProductSchema = yup.object().shape({
  name: yup
    .string()
    .required(getMessage.name_required)
    .matches(/^[a-zA-Z0-9\s\-\.@(),/&#]*$/, getMessage.name_matches)
    .min(3, getMessage.name_minLength)
    .max(30, getMessage.name_maxLength),
  hsnNumber: yup
    .string()
    .required(getMessage.hsnNumber_required)
    .matches(/^[0-9]\d{5,7}$/, getMessage.hsnNumber_min_max_length),
  totalQuantity: yup
    .number()
    .required(getMessage.totalQuantity_required)
    .typeError(getMessage.totalQuantity_required)
    .min(1, "Minimum quantity is 1")
    .integer("Quantity must be a whole number")
    .max(9999, getMessage.productQuantity_length
    ),
  costPrice: yup
    .string()
    .typeError(getMessage.costPrice_required)
    .required(getMessage.costPrice_required)
    .matches(/^\d+(\.\d{1,2})?$/, getMessage.costPrice_matches)

    .max(10, getMessage.costPrice_maxLength)
    .test(
      "minimum value",
      getMessage.costPrice_minValue,
      (val) => !(Number(val) <= 0)
    ),
  sellingPrice: yup
    .string()
    .required(getMessage.sellingPricePerUnit_required)
    .matches(/^\d+(\.\d{1,2})?$/, getMessage.sellingPrice_matches)
    .max(10, getMessage.sellingPrice_maxLength),
});

const AddSellingInventory = () => {
  const { SelectedFacility } = useAppSelector((state) => state.facilityList);
  const navigate = useNavigate();
  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<SellInventoryType>({
    resolver: yupResolver(AddProductSchema),
  });
  const [submit, setSubmit] = useState<boolean>(false);
  const [igst, setIgst] = useState<number | string>("");
  const [sgst, setSgst] = useState<number | string>("");
  const [cgst, setCgst] = useState<number | string>("");
  const [sellingPrice, setSellingPrice] = useState<number | string>("");
  const [errorMessage, setErrorMessage] = useState({
    igst: "",
    cgst: "",
    sgst: "",
    sellingPrice: "",
  });

  const onSubmit: SubmitHandler<SellInventoryType> = async (data) => {
    if (validate()) {
      let payload = {
        ...data,
        facilityId: Number(SelectedFacility),
        isActive: "true",
        igst: Number(igst),
        cgst: Number(cgst),
        sgst: Number(sgst),
        costPrice: Number(data.costPrice),
        sellingPrice: Number(data.sellingPrice),
      };
      const result = await CreateSellingProduct(payload);
      if (result?.status === 200) {
        navigate("/sell-stock");
      } else {
        toast.error(result.response.data.message);
      }
    }
  };

  const validate = () => {
    setSubmit(true);
    let Igst = Number(igst);
    let Cgst = Number(cgst);
    let Sgst = Number(sgst);
    if (!SelectedFacility) {
      toast.info(getMessage.facility_select);
    } else if (Igst <= 0 || Cgst <= 0 || Sgst <= 0) {
      setErrorMessage({
        igst:
          Igst <= 0
            ? getMessage.IGST_required
            : numberValidation(Igst) != null
              ? ""
              : getMessage.IGST_valueType,
        cgst:
          Cgst <= 0
            ? getMessage.CGST_required
            : numberValidation(Cgst) != null
              ? ""
              : getMessage.CGST_valueType,
        sgst:
          Sgst <= 0
            ? getMessage.SGST_required
            : numberValidation(Sgst) != null
              ? ""
              : getMessage.SGST_valueType,
        sellingPrice: "",
      });
      return false;
    } else {
      setErrorMessage({ igst: "", cgst: "", sgst: "", sellingPrice: "" });
      return true;
    }
  };

  useEffect(() => {
    if (submit) {
      validate();
    }
  }, [sellingPrice, igst]);
  return (
    <>
      {" "}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Container fluid className="g-0">
          <Card>
            <Card.Body>
              <Card.Title className="text-start">Add Sales Stock</Card.Title>
              <Row>
                <Col col={6}>
                  <div className="mb-3 text-start">
                    <label htmlFor="name" className="form-label">
                      Product Name <small className="text-danger">*</small>
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter Product Name"
                      id="name"
                      name="name"
                      className="form-control"
                      register={register}
                      required
                    />
                    <span className="error-msg">{errors?.name?.message}</span>
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="mb-3 text-start">
                    <label htmlFor="totalQuantity" className="form-label">
                      Product Quantity <small className="text-danger">*</small>
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter Product Quantity"
                      id="totalQuantity"
                      name="totalQuantity"
                      className="form-control"
                      register={register}
                      required
                      onKeyDown={(e: any) =>
                        ["e", ".", "-", "+"].includes(e.key) &&
                        e.preventDefault()
                      }
                      onWheel={(e: any) => e.target.blur()}
                    />
                    <span className="error-msg">
                      {errors?.totalQuantity?.message}
                    </span>
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="mb-3 text-start">
                    <label htmlFor="costPrice" className="form-label">
                      Purchased Price per Unit{" "}
                      <small className="text-danger">*</small>
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter Price"
                      id="costPrice"
                      name="costPrice"
                      className="form-control"
                      register={register}
                      required
                      onKeyDown={(e: any) =>
                        e.key === "e" && e.preventDefault()
                      }
                    />
                    <span className="error-msg">
                      {errors?.costPrice?.message}
                    </span>
                  </div>
                </Col>
                <Col lg={6}>
                  <div className="mb-3 text-start">
                    <label htmlFor="sellingPrice" className="form-label">
                      Selling Price per Unit{" "}
                      <small className="text-danger">*</small>
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter Price"
                      id="sellingPrice"
                      name="sellingPrice"
                      className="form-control"
                      register={register}
                      required
                      onChange={(e: any) => setSellingPrice(e.target.value)}
                      onKeyDown={(e: any) =>
                        e.key === "e" && e.preventDefault()
                      }
                    />
                    <span className="error-msg">
                      {errors?.sellingPrice?.message}
                      {errorMessage.sellingPrice}
                    </span>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={3}>
                  <div className="mb-3 text-start">
                    <label htmlFor="hsnNumber" className="form-label">
                      HSN Number <small className="text-danger">*</small>
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter HSN Number"
                      id="hsnNumber"
                      name="hsnNumber"
                      className="form-control"
                      register={register}
                      required
                      onKeyDown={(e: any) => {
                        if (["e", ".", "-", "+"].includes(e.key)) e.preventDefault();
                        const val = e.target.value;
                        if (val.length >= 8 && e.key !== "Backspace" && e.key !== "Delete" && e.key !== "Tab" && e.key !== "ArrowLeft" && e.key !== "ArrowRight") e.preventDefault();
                      }}
                      onWheel={(e: any) => e.target.blur()}
                    />
                    <span className="error-msg">
                      {errors?.hsnNumber?.message}
                    </span>
                  </div>
                </Col>
                <Col lg={2}>
                  <div className="mb-3 text-start">
                    <label htmlFor="igst" className="form-label">
                      IGST (%) <small className="text-danger">*</small>
                    </label>
                    <Input
                      type="number"
                      pattern="^\d*(\.\d{0,2})?$"
                      placeholder="Enter IGST"
                      id="igst"
                      name="igst"
                      className="form-control"
                      value={igst}
                      onChange={(e: any) =>
                        handleIgstChange(e, setIgst, setCgst, setSgst)
                      }
                    />
                    <span className="error-msg">{errorMessage.igst}</span>
                  </div>
                </Col>
                <Col lg={2}>
                  <div className="mb-3 text-start">
                    <label htmlFor="cgst" className="form-label">
                      CGST (%) <small className="text-danger">*</small>
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter CGST"
                      id="cgst"
                      name="cgst"
                      pattern="^\d*(\.\d{0,2})?$"
                      className="form-control"
                      value={cgst}
                      onChange={(e: any) =>
                        handleCgstChange(e, setIgst, setCgst, sgst)
                      }
                    />
                    <span className="error-msg">{errorMessage.cgst}</span>
                  </div>
                </Col>
                <Col lg={2}>
                  <div className="mb-3 text-start">
                    <label htmlFor="s-gst" className="form-label">
                      SGST (%) <small className="text-danger">*</small>
                    </label>
                    <Input
                      type="number"
                      placeholder="Enter SGST"
                      id="sgst"
                      name="sgst"
                      pattern="^\d*(\.\d{0,2})?$"
                      className="form-control"
                      value={sgst}
                      onChange={(e: any) =>
                        handleSgstChange(e, setIgst, cgst, setSgst)
                      }
                    />
                    <span className="error-msg">{errorMessage.sgst}</span>
                  </div>
                </Col>
                <Col lg={3} className="align-self-end">
                  <div className="badge bg-info fs-6 fst-italic fw-semibold mb-0 mb-3 p-2 text-nowrap">
                    Selling Price Per Unit:{" "}
                    <span className="rupees-symbol">&#8377;</span>{" "}
                    {(
                      Number(sellingPrice) +
                      getPercentValue(Number(sellingPrice), Number(igst))
                    ).toFixed(2)}
                  </div>
                </Col>
              </Row>
              <hr />
              <Row>
                <Col className="text-end">
                  <Button
                    onClick={() => validate()}
                    type="submit"
                    bname="Submit"
                    name="submit"
                    id="submit"
                    className="btn btn-primary rounded-1"
                  ></Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Container>
      </form>
    </>
  );
};
export { AddSellingInventory };
